/**
 * 基建渲染层 —— 道路 / 电缆 / 管道的合并弧带。
 *
 * 这是把 `main.ts` 拆成模块的第四刀。前三刀是 terrain-profile / camera-rig /
 * building-pool；挑这块是因为它刚刚被整体重写（逐格 mesh → 合并几何），
 * 边界正好在重写后变得清晰：**渲染层不再持有任何拓扑记账**，全部从内核读。
 *
 * ## 为什么是合并几何而不是 InstancedMesh
 *
 * 建筑用 InstancedMesh（见 building-pool.ts）：同一个几何体，逐实例只差一个
 * 变换矩阵。弧带不行 —— 每格弧带的**半径和弧长都随环变化**，几何体本身不同，
 * 无法共享。
 *
 * 但它们适合另一条路：同层的全部段落拼进一个 `BufferGeometry`，拓扑变化时
 * 整体重建。重建 210 段约 7000 顶点，只在铺设/拆除时发生，而绘制调用从
 * 每格一次降到每层一次。实测 804 → 288。
 *
 * ## 为什么逐点采样地形高度
 *
 * 陨坑起伏很大（坑底到坑缘 264m）。固定 y 的平面环在外环会整段埋进地里或
 * 悬空，因此弧带的每个顶点都要取 `craterHeight`。
 */

import * as THREE from 'three';
import * as K from './kernel-bridge';
import { craterHeight } from './terrain-profile';

/* eslint-disable @typescript-eslint/no-explicit-any -- Three.js 对象在本文件按运行时形状使用，与既有渲染代码一致 */
type AnyObj = any;

/** 道路弧带宽度。预览网格也用它，故导出。 */
export const ROAD_W = 15;

/** 弧带的圆周细分数。16 段在最外环仍看不出折线。 */
const SEG = 16;

interface InfraDeps {
  /** 所有基建网格挂载的场景组。 */
  readonly city: AnyObj;
  /** 几何变了要重画阴影贴图。 */
  readonly shadowDirty: () => void;
}

let deps: InfraDeps | null = null;

export function initInfrastructure(d: InfraDeps): void {
  deps = d;
}

function need(): InfraDeps {
  if (!deps) throw new Error('infrastructure: initInfrastructure() 未调用');
  return deps;
}

interface Cell {
  readonly r: number;
  readonly sct: number;
}

/** 把若干格拼成一个几何体。空数组返回空几何，调用方负责不挂它。 */
function ribbonMerged(cells: readonly Cell[], width: number, yOff: number): AnyObj {
  const pos: number[] = [];
  const idx: number[] = [];
  for (const { r, sct } of cells) {
    const rr = K.RING_R0 + r * K.RING_STEP;
    const n = K.secOf(r);
    const a0 = (K.wrapSct(r, sct) / n) * Math.PI * 2;
    const aw = (Math.PI * 2) / n;
    const base = pos.length / 3;
    for (let i = 0; i <= SEG; i++) {
      const a = a0 + aw * (i / SEG);
      const ca = Math.cos(a);
      const sa = Math.sin(a);
      for (const sgn of [-1, 1]) {
        const rad = rr + (sgn * width) / 2;
        const x = ca * rad;
        const z = sa * rad;
        pos.push(x, craterHeight(x, z) + yOff, z);
      }
    }
    for (let i = 0; i < SEG; i++) {
      const b = base + i * 2;
      idx.push(b, b + 1, b + 2, b + 1, b + 3, b + 2);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
}

/** 一层合并弧带：持有单个 Mesh，按需整体重建。 */
class RibbonLayer {
  private mesh: AnyObj = null;

  constructor(
    private readonly mat: AnyObj,
    private readonly width: number,
    private readonly yOff: number,
  ) {}

  rebuild(cells: readonly Cell[]): void {
    const { city } = need();
    if (this.mesh) {
      city.remove(this.mesh);
      this.mesh.geometry.dispose();
      this.mesh = null;
    }
    if (cells.length === 0) return; // 空层不留 mesh，省一次绘制提交
    this.mesh = new THREE.Mesh(ribbonMerged(cells, this.width, this.yOff), this.mat);
    city.add(this.mesh);
  }

  /** 清场：几何释放，材质是共享常量不动。 */
  dispose(): void {
    this.rebuild([]);
  }
}

/* ---------- 材质。只被本模块的层使用；预览用的高亮材质留在 main。 ---------- */
const cableMat = new THREE.MeshBasicMaterial({ color: 0xffc247, transparent: true, opacity: .75,
  side: THREE.DoubleSide, depthWrite: false, blending: THREE.AdditiveBlending });
const pipeMat = new THREE.MeshBasicMaterial({ color: 0x4fd2ff, transparent: true, opacity: .6,
  side: THREE.DoubleSide, depthWrite: false, blending: THREE.AdditiveBlending });
const pipeShutMat = new THREE.MeshBasicMaterial({ color: 0xff5d5d, transparent: true, opacity: .7,
  side: THREE.DoubleSide, depthWrite: false, blending: THREE.AdditiveBlending });
const roadBandMat = new THREE.MeshBasicMaterial({ color: 0x2fb6dd, transparent: true, opacity: .30,
  side: THREE.DoubleSide, depthWrite: false, blending: THREE.AdditiveBlending });
const roadEdgeMat = new THREE.MeshBasicMaterial({ color: 0x8ff2ff, transparent: true, opacity: .85,
  side: THREE.DoubleSide, depthWrite: false });

const layers = {
  roadBand: new RibbonLayer(roadBandMat, ROAD_W, .8),
  roadEdge: new RibbonLayer(roadEdgeMat, 2.2, 1.1),
  cable: new RibbonLayer(cableMat, 3.2, 1.6),
  pipe: new RibbonLayer(pipeMat, 2.4, 2.2),
  pipeShut: new RibbonLayer(pipeShutMat, 2.4, 2.2), // 阀门关闭的管段另一种颜色
} as const;

/* ---------- 脏标记 ---------- */

let dirty = false;

/**
 * 标记基建拓扑已变。
 *
 * 只置位，不重建 —— 播种示范殖民地会连续标脏 400 多次，若每次都重建就是
 * 400 次全量重建。真正的重建由 `flushInfrastructure()` 每帧至多做一次。
 */
export function markInfraDirty(): void {
  dirty = true;
}

/** 主循环每帧调用。拓扑没变就是一次布尔判断。 */
export function flushInfrastructure(): void {
  if (!dirty) return;
  dirty = false;
  rebuildInfraLayers();
}

/** 遍历全网格，按内核状态收集各层的格子并整体重建。 */
export function rebuildInfraLayers(): void {
  const road: Cell[] = [];
  const cable: Cell[] = [];
  const pipe: Cell[] = [];
  const pipeShut: Cell[] = [];
  for (let r = 0; r < K.RING_COUNT; r++) {
    const n = K.secOf(r);
    for (let sct = 0; sct < n; sct++) {
      if (K.hasRoad(r, sct)) road.push({ r, sct });
      if (K.hasCable(r, sct)) cable.push({ r, sct });
      if (K.hasPipe(r, sct)) (K.valveClosed(r, sct) ? pipeShut : pipe).push({ r, sct });
    }
  }
  layers.roadBand.rebuild(road);
  layers.roadEdge.rebuild(road);
  layers.cable.rebuild(cable);
  layers.pipe.rebuild(pipe);
  layers.pipeShut.rebuild(pipeShut);
  need().shadowDirty();
}

/** 清场（返回主菜单 / 读档前）。 */
export function disposeInfrastructure(): void {
  for (const L of Object.values(layers)) L.dispose();
  dirty = false;
}

/** 统计内核里某类基建的格数。合并几何后没有逐格 mesh 可数，只能问内核。 */
export function countInfra(has: (r: number, sct: number) => boolean): number {
  let n = 0;
  for (let r = 0; r < K.RING_COUNT; r++) {
    const m = K.secOf(r);
    for (let sct = 0; sct < m; sct++) if (has(r, sct)) n++;
  }
  return n;
}
